import React, { useState, useEffect } from 'react';
import { Download, RefreshCw, Settings, Image as ImageIcon, Palette, Sparkles } from 'lucide-react';
import { buildCloudinaryUrl, downloadFromUrl, estimateFileSize, validateTransformOptions } from '../../utils/cloudinaryClient';

const LogoTransformPanel = ({ logo, onClose }) => {
  // Transformation state
  const [format, setFormat] = useState('png');
  const [width, setWidth] = useState(512);
  const [height, setHeight] = useState(512);
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const [bgType, setBgType] = useState('transparent');
  const [bgColor, setBgColor] = useState('#ffffff');
  const [colorless, setColorless] = useState(false);
  const [quality, setQuality] = useState(90);
  const [useBackgroundRemoval, setUseBackgroundRemoval] = useState(false);
  
  // UI state
  const [previewUrl, setPreviewUrl] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState('');
  const [estimatedSize, setEstimatedSize] = useState('');
  const [activeTab, setActiveTab] = useState('basic');

  // Original dimensions
  const originalWidth = logo.width || logo.dimensions?.width || 512;
  const originalHeight = logo.height || logo.dimensions?.height || 512;
  const aspectRatio = originalWidth / originalHeight;

  // Update preview URL when options change
  useEffect(() => {
    updatePreview();
  }, [format, width, height, bgType, bgColor, colorless, quality, useBackgroundRemoval]);

  // Update estimated size
  useEffect(() => {
    const size = estimateFileSize(width, height, format);
    setEstimatedSize(size);
  }, [width, height, format]);

  const updatePreview = () => {
    if (!logo.public_id && !logo.secure_url) {
      setError('Logo does not have Cloudinary data');
      return;
    }

    const options = {
      format,
      width,
      height,
      backgroundType: bgType,
      bgColor: bgType === 'colored' ? bgColor : undefined,
      colorless,
      quality,
      useBackgroundRemoval: bgType === 'transparent' && useBackgroundRemoval,
      crop: 'fit'
    };

    // Validate options
    const validation = validateTransformOptions(options);
    if (!validation.valid) {
      setError(validation.errors.join(', '));
      return;
    }

    setError('');

    // For SVG or PDF format, handle specially
    if (format === 'svg' || format === 'pdf') {
      if (logo.is_svg && format === 'svg') {
        // Show original SVG
        setPreviewUrl(logo.secure_url || logo.path);
      } else if (logo.public_id) {
        // Extract cloud name from secure_url if available
        const cloudName = logo.secure_url ? 
          logo.secure_url.match(/res\.cloudinary\.com\/([^\/]+)/)?.[1] : null;
        
        // For PDF or SVG conversion from raster, still build URL
        const url = buildCloudinaryUrl(logo.public_id, options, cloudName);
        console.log('Preview URL (SVG/PDF):', url); // Debug log
        setPreviewUrl(url);
      } else {
        setPreviewUrl(logo.secure_url || logo.path);
      }
      return;
    }

    // Build transformation URL for raster formats
    if (logo.public_id) {
      // Extract cloud name from secure_url if available
      const cloudName = logo.secure_url ? 
        logo.secure_url.match(/res\.cloudinary\.com\/([^\/]+)/)?.[1] : null;
      
      const url = buildCloudinaryUrl(logo.public_id, options, cloudName);
      console.log('Preview URL:', url); // Debug log
      setPreviewUrl(url);
    } else {
      // Fallback to original URL
      setPreviewUrl(logo.secure_url || logo.path);
    }
  };

  const handleWidthChange = (newWidth) => {
    setWidth(newWidth);
    if (maintainAspectRatio) {
      setHeight(Math.round(newWidth / aspectRatio));
    }
  };

  const handleHeightChange = (newHeight) => {
    setHeight(newHeight);
    if (maintainAspectRatio) {
      setWidth(Math.round(newHeight * aspectRatio));
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    setError('');

    try {
      // Generate structured filename based on user specifications
      const generateFilename = () => {
        const parts = [];
        
        // Logo name (sanitized)
        const baseName = logo.name.replace(/[^a-zA-Z0-9-_]/g, '-').replace(/-+/g, '-').toLowerCase();
        parts.push(baseName);
        
        // Dimensions (skip for SVG as it's vector)
        if (format !== 'svg') {
          parts.push(`${width}x${height}`);
          
          // Background info
          if (bgType === 'transparent') {
            parts.push('transparent');
          } else if (bgType === 'colored') {
            parts.push(`bg-${bgColor.replace('#', '')}`);
          }
          
          // Effects
          if (colorless) {
            parts.push('grayscale');
          }
          
          // Quality for raster formats
          if (['png', 'jpg', 'jpeg', 'webp'].includes(format) && quality !== 90) {
            parts.push(`q${quality}`);
          }
        }
        
        return `${parts.join('_')}.${format}`;
      };

      // Special handling for SVG format
      if (format === 'svg' && logo.is_svg) {
        // Download original SVG directly
        const filename = generateFilename();
        const svgUrl = logo.secure_url || logo.path;
        await downloadFromUrl(svgUrl, filename);
        setIsDownloading(false);
        return;
      }

      const options = {
        format,
        width,
        height,
        backgroundType: bgType,
        bgColor: bgType === 'colored' ? bgColor : undefined,
        colorless,
        quality,
        useBackgroundRemoval: bgType === 'transparent' && useBackgroundRemoval
      };

      // If using MongoDB-based logo with API
      if (logo._id) {
        const response = await fetch(`/api/cloudinary/logos/${logo._id}/download`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(options)
        });

        if (!response.ok) {
          throw new Error('Download preparation failed');
        }

        const data = await response.json();
        const filename = generateFilename();
        await downloadFromUrl(data.url, filename);
      } else {
        // Direct download from preview URL
        const filename = generateFilename();
        await downloadFromUrl(previewUrl, filename);
      }

    } catch (err) {
      console.error('Download error:', err);
      setError('Download failed. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const resetToDefaults = () => {
    setFormat('png');
    setWidth(512);
    setHeight(512);
    setMaintainAspectRatio(true);
    setBgType('transparent');
    setBgColor('#ffffff');
    setColorless(false);
    setQuality(90);
    setUseBackgroundRemoval(false);
  };

  return (
    <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-50 to-purple-50">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{logo.name}</h2>
          <p className="text-sm text-gray-600 mt-1">Transform & Download</p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto flex flex-col lg:flex-row">
        {/* Preview Section */}
        <div className="lg:w-1/2 p-6 bg-gray-50 flex flex-col items-center justify-center min-h-[300px]">
          <div className="relative w-full max-w-md">
            <div className="aspect-square bg-white rounded-lg shadow-lg p-8 flex items-center justify-center">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt={`${logo.name} preview`}
                  className="max-w-full max-h-full object-contain"
                  onError={() => setError('Failed to load preview')}
                />
              ) : (
                <div className="text-gray-400 text-center">
                  <ImageIcon className="w-16 h-16 mx-auto mb-2" />
                  <p>Preview will appear here</p>
                </div>
              )}
            </div>
            
            {/* Preview Info */}
            <div className="mt-4 text-center text-sm text-gray-600">
              {format === 'svg' ? (
                <>
                  <p className="font-medium text-blue-600">Vector Format (Scalable)</p>
                  <p className="font-medium text-gray-900">Est. size: {estimatedSize}</p>
                </>
              ) : format === 'pdf' ? (
                <>
                  <p className="font-medium text-red-600">PDF Document</p>
                  <p className="font-medium text-gray-900">Est. size: {estimatedSize}</p>
                </>
              ) : (
                <>
                  <p>{width} × {height} px</p>
                  <p className="font-medium text-gray-900">Est. size: {estimatedSize}</p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Controls Section */}
        <div className="lg:w-1/2 p-6 overflow-y-auto">
          {/* Tabs */}
          <div className="flex space-x-2 mb-6 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('basic')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === 'basic'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Basic
            </button>
            <button
              onClick={() => setActiveTab('advanced')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === 'advanced'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Sparkles className="w-4 h-4 inline mr-2" />
              Advanced
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Basic Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-6">
              {/* Format Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Format
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {['svg', 'png', 'jpg', 'webp', 'pdf'].map((fmt) => (
                    <button
                      key={fmt}
                      onClick={() => setFormat(fmt)}
                      className={`px-4 py-2 rounded-lg font-medium transition-all ${
                        format === fmt
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {fmt.toUpperCase()}
                    </button>
                  ))}
                </div>
                {logo.is_svg && format === 'svg' && (
                  <p className="mt-2 text-xs text-blue-600">
                    Original SVG will be downloaded (no transformation)
                  </p>
                )}
                {format === 'pdf' && (
                  <p className="mt-2 text-xs text-gray-600">
                    PDF format is great for printing and documentation
                  </p>
                )}
              </div>

              {/* Dimensions */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Dimensions
                  </label>
                  <label className="flex items-center text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={maintainAspectRatio}
                      onChange={(e) => setMaintainAspectRatio(e.target.checked)}
                      className="mr-2"
                    />
                    Lock aspect ratio
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Width (px)</label>
                    <input
                      type="number"
                      value={width}
                      onChange={(e) => handleWidthChange(Number(e.target.value))}
                      min="1"
                      max="5000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Height (px)</label>
                    <input
                      type="number"
                      value={height}
                      onChange={(e) => handleHeightChange(Number(e.target.value))}
                      min="1"
                      max="5000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                {/* Quick size presets */}
                <div className="mt-2 flex flex-wrap gap-2">
                  {[128, 256, 512, 1024, 2048].map((size) => (
                    <button
                      key={size}
                      onClick={() => {
                        setWidth(size);
                        setHeight(size);
                      }}
                      className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                    >
                      {size}×{size}
                    </button>
                  ))}
                </div>
              </div>

              {/* Background */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Background
                </label>
                <div className="space-y-3">
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="transparent"
                        checked={bgType === 'transparent'}
                        onChange={(e) => setBgType(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Transparent</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="colored"
                        checked={bgType === 'colored'}
                        onChange={(e) => setBgType(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Colored</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="original"
                        checked={bgType === 'original'}
                        onChange={(e) => setBgType(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Original</span>
                    </label>
                  </div>

                  {bgType === 'colored' && (
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={bgColor}
                        onChange={(e) => setBgColor(e.target.value)}
                        className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={bgColor}
                        onChange={(e) => setBgColor(e.target.value)}
                        placeholder="#ffffff"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  )}

                  {(format === 'jpg' || format === 'jpeg') && bgType === 'transparent' && (
                    <p className="text-xs text-yellow-600">
                      ⚠️ JPG format does not support transparency. A white background will be applied.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Advanced Tab */}
          {activeTab === 'advanced' && (
            <div className="space-y-6">
              {/* Quality */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quality: {quality}%
                </label>
                <input
                  type="range"
                  min="1"
                  max="100"
                  value={quality}
                  onChange={(e) => setQuality(Number(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Lower size</span>
                  <span>Higher quality</span>
                </div>
              </div>

              {/* Effects */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Effects
                </label>
                <div className="space-y-3">
                  <label className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                    <input
                      type="checkbox"
                      checked={colorless}
                      onChange={(e) => setColorless(e.target.checked)}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium text-sm">Grayscale</div>
                      <div className="text-xs text-gray-600">Convert to black and white</div>
                    </div>
                  </label>

                  {bgType === 'transparent' && (
                    <label className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                      <input
                        type="checkbox"
                        checked={useBackgroundRemoval}
                        onChange={(e) => setUseBackgroundRemoval(e.target.checked)}
                        className="mr-3"
                      />
                      <div>
                        <div className="font-medium text-sm">Remove Background</div>
                        <div className="text-xs text-gray-600">
                          AI-powered background removal (requires Cloudinary add-on)
                        </div>
                      </div>
                    </label>
                  )}
                </div>
              </div>

              {/* Logo Info */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-sm text-blue-900 mb-2">Original Info</h4>
                <div className="text-xs text-blue-700 space-y-1">
                  <p>Format: {logo.original_format || 'Unknown'}</p>
                  <p>Dimensions: {originalWidth} × {originalHeight} px</p>
                  {logo.is_svg && <p className="font-medium">Vector (SVG) - Scalable</p>}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-8 space-y-3">
            <button
              onClick={handleDownload}
              disabled={isDownloading || !!error}
              className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
            >
              {isDownloading ? (
                <>
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5 mr-2" />
                  Download {format.toUpperCase()}
                </>
              )}
            </button>

            <button
              onClick={resetToDefaults}
              className="w-full px-6 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
            >
              Reset to Defaults
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogoTransformPanel;
